import React, { useCallback, useEffect, useState } from 'react';

import BudgetTagsSection from './BudgetTagsSection';
import { budgetTagsErrorMessages, fetchItemBudgetTags } from './itemBudgetTagsApi';

import LoadingSpinner from 'CommonComponents/LoadingSpinner';

const isBlank = (value) => value === null || value === undefined || value === '';

// Rows are ignored while the item follows its header, so every "off" state is the same.
// Blank rows are dropped, as the server drops them.
const tagsKey = (value) =>
    value.overrides
        ? JSON.stringify(
              (value.rows || []).filter((row) => !(isBlank(row.tag_id) && isBlank(row.percentage))).map((row) => [String(row.tag_id), Number(row.percentage)]),
          )
        : 'follows-header';

/**
 * Loads a template or pack item's tags editor and reports the edited { overrides, rows }
 * through `onChange`, or null while there is nothing to save: still loading, read-only, or
 * unchanged from what was loaded. Without `packId` a pack item lists the tags of every
 * pack it sits in.
 */
export default function BudgetTagsItemEditor({ id, url, itemId, packId = null, disabled, onChange }) {
    const [editor, setEditor] = useState(null);
    const [loadError, setLoadError] = useState(null);

    useEffect(() => {
        setEditor(null);
        setLoadError(null);
        onChange(null);
        fetchItemBudgetTags(url, itemId, packId)
            .then(setEditor)
            .catch((error) => setLoadError(budgetTagsErrorMessages(error)[0]));
    }, [url, itemId, packId]);

    const loadedKey = editor ? tagsKey({ overrides: !!editor.form.overrides, rows: editor.form.rows }) : null;
    const handleChange = useCallback((value) => onChange(tagsKey(value) === loadedKey ? null : value), [onChange, loadedKey]);

    if (loadError) {
        return <p className="tw-text-red tw-text-sm tw-mb-0">{loadError}</p>;
    }

    if (!editor) {
        return <LoadingSpinner />;
    }

    const readOnly = disabled || !editor.cell.editable;

    return <BudgetTagsSection config={editor.form} i18n={editor.labels} disabled={readOnly} idPrefix={id} onChange={readOnly ? undefined : handleChange} />;
}
